/**
 * WebSocket 구독 관리 Composable
 *
 * 관리하는 채널:
 * - market.quote: 시장 시세 데이터
 * - market.ticker: 시장 OHLC 데이터 (고가/저가/전일종가)
 * - private: 계좌별 주문/체결 등의 데이터
 * - market.orderbook, market.orderbook.cancel: 5단계 호가 데이터
 *
 * 구독/구독 해제 시 추가 정보:
 * - market.orderbook, market.orderbook.cancel는 options에 항상 stockCd 정보 같이 전달 (공개 채널, 로그인 불필요)
 * - market.quote, market.ticker는 공개 채널이므로 로그인 없이도 구독 가능 (token 불필요)
 * - private는 계좌별 데이터이므로 계좌가 있을 때만 구독 (token 필요)
 * - market.quote, market.ticker와 private는 모두 구독해야 하는 경우(account가 있는 경우)에 한 번에 구독 (token을 한 번만 보내기 위해)
 *
 * 재구독 시나리오:
 * - 종목 변경 시: market.orderbook, market.orderbook.cancel 재구독
 * - 계좌 변경 시: 이전 계좌에 대한 토큰 제거 후, API를 통해 새로운 토큰 발급받아야 함
 *   토큰 발급이 완료된 이후에 해당 토큰을 이용해서 market.quote, market.ticker, private 재구독
 */
import { ref } from 'vue';
// 전역 의존성 설정
let globalDeps = null;
/**
 * WebSocket 구독 관리를 위한 전역 의존성 설정
 * 앱 초기화 시 한 번만 호출
 */
export function setWebSocketSubscriptionDependencies(deps) {
    globalDeps = deps;
}
/** 계좌 토큰이 필요한 채널 (market.quote, market.ticker, private) */
const ACCOUNT_TOKEN_CHANNELS = ['market.quote', 'market.ticker', 'private'];
/** 계좌 없을 때 구독하는 공개 채널 (market.quote, market.ticker) */
const PUBLIC_CHANNELS = ['market.quote', 'market.ticker'];
// 전역 구독 상태 (모든 인스턴스에서 공유)
const globalSubscribedChannels = ref(new Set());
// 이전 계좌 번호 추적 (계좌 변경 감지용)
const previousAccountNo = ref(undefined);
// 이전 토큰 추적 (계좌 변경 시 이전 계좌 구독 해제용)
const previousAccountToken = ref(null);
// 이전 종목 추적 (종목 변경 감지용)
const previousSymbol = ref(undefined);
export function useWebSocketSubscription(deps) {
    // 전역 설정이 있으면 사용, 없으면 파라미터로 받은 의존성 사용
    const finalDeps = deps || globalDeps;
    if (!finalDeps) {
        throw new Error('WebSocket 구독 의존성이 설정되지 않았습니다. setWebSocketSubscriptionDependencies를 먼저 호출하거나 deps를 전달하세요.');
    }
    const webSocketService = finalDeps.getWebSocketService();
    // 전역 구독 상태 사용
    const subscribedChannels = globalSubscribedChannels;
    /**
     * 계좌 변경 시 채널 구독 해제 및 재구독
     * market.quote, market.ticker, private 채널은 계좌별 JWT 토큰이 필요하므로
     * 계좌 변경 시 이전 토큰으로 구독 해제 후 새 토큰으로 재구독해야 함
     *
     * @returns 계좌 변경이 처리되었으면 true (호출자가 후속 로직 스킵 가능)
     */
    const handleAccountChangeResubscription = (params) => {
        const { currentAccount, accountToken } = params;
        const isAccountChanged = previousAccountNo.value !== undefined && previousAccountNo.value !== currentAccount;
        if (!isAccountChanged) {
            previousAccountNo.value = currentAccount;
            previousAccountToken.value = accountToken;
            return false;
        }
        // 이전 계좌 구독 해제
        if (previousAccountNo.value && previousAccountToken.value) {
            const channelsToUnsubscribe = ACCOUNT_TOKEN_CHANNELS.filter((ch) => subscribedChannels.value.has(ch));
            if (channelsToUnsubscribe.length > 0) {
                webSocketService.unsubscribeFromChannels(channelsToUnsubscribe, {
                    token: `Bearer ${previousAccountToken.value}`,
                });
                channelsToUnsubscribe.forEach((ch) => subscribedChannels.value.delete(ch));
            }
        }
        // 새 계좌 구독
        if (currentAccount && accountToken) {
            webSocketService.subscribeToChannels(ACCOUNT_TOKEN_CHANNELS, {
                token: `Bearer ${accountToken}`,
            });
            ACCOUNT_TOKEN_CHANNELS.forEach((ch) => subscribedChannels.value.add(ch));
        }
        else if (currentAccount && !accountToken) {
            console.warn('[useWebSocketSubscription] 새 계좌 인증 대기 중...');
            previousAccountNo.value = currentAccount;
            previousAccountToken.value = accountToken;
            return true;
        }
        else {
            const needsSubscription = PUBLIC_CHANNELS.some((ch) => !subscribedChannels.value.has(ch));
            if (needsSubscription) {
                webSocketService.subscribeToChannels(PUBLIC_CHANNELS);
                PUBLIC_CHANNELS.forEach((ch) => subscribedChannels.value.add(ch));
            }
        }
        previousAccountNo.value = currentAccount;
        previousAccountToken.value = accountToken;
        return true;
    };
    /**
     * WebSocket 연결 상태 확인 및 필요 시 연결
     */
    const ensureWebSocketConnection = async () => {
        try {
            if (!finalDeps.isConnected()) {
                await finalDeps.initializeGlobalWebSocket();
            }
        }
        catch (error) {
            console.error('[useWebSocketSubscription] 전역 WebSocket 연결 확인/설정 실패:', error);
            throw error;
        }
    };
    /**
     * 주문 화면용 WebSocket 채널 구독 업데이트
     *
     * @param selectedSymbol - 선택된 종목 (null이면 orderbook 채널 구독하지 않음)
     */
    const updateWebSocketSubscriptions = async (selectedSymbol) => {
        // WebSocket 연결 상태 확인 및 필요 시 연결
        await ensureWebSocketConnection();
        const currentAccount = finalDeps.getSelectedAccountNo();
        const accountToken = finalDeps.getAccountToken();
        const currentSymbolTicker = selectedSymbol?.ticker;
        // 계좌 변경 감지
        const isAccountChanged = previousAccountNo.value !== undefined && previousAccountNo.value !== currentAccount;
        // 종목 변경 감지
        const isSymbolChanged = previousSymbol.value !== undefined &&
            previousSymbol.value !== currentSymbolTicker &&
            (previousSymbol.value || currentSymbolTicker);
        try {
            // 1. 계좌 변경 시: market.quote, market.ticker, private 재구독
            const accountChangeHandled = handleAccountChangeResubscription({
                currentAccount,
                accountToken,
            });
            if (accountChangeHandled) {
                return; // 계좌 변경 처리 완료, 종목 변경은 다음 호출에서 처리
            }
            // 2. 종목 변경 시: market.orderbook, market.orderbook.cancel 재구독
            if (isSymbolChanged && currentSymbolTicker) {
                const orderBookChannels = [
                    'market.orderbook',
                    'market.orderbook.cancel',
                ];
                // 이전 종목의 orderbook 채널 구독 해제 (이전 종목이 있고 구독 중인 경우만)
                if (previousSymbol.value) {
                    const channelsToUnsub = orderBookChannels.filter((ch) => subscribedChannels.value.has(ch));
                    if (channelsToUnsub.length > 0) {
                        webSocketService.unsubscribeFromChannels(channelsToUnsub, {
                            stockCd: previousSymbol.value,
                        });
                        channelsToUnsub.forEach((ch) => subscribedChannels.value.delete(ch));
                    }
                }
                // 새 종목의 orderbook 채널 구독 (ws 토큰은 항상 전달)
                webSocketService.subscribeToChannels(orderBookChannels, {
                    stockCd: currentSymbolTicker,
                    ...(accountToken && { token: `Bearer ${accountToken}` }),
                });
                orderBookChannels.forEach((ch) => subscribedChannels.value.add(ch));
                // 종목 변경 처리 완료 후 이전 값 업데이트
                previousSymbol.value = currentSymbolTicker;
                return; // 종목 변경 처리 완료
            }
            // 3. 초기 구독 또는 계좌/종목 변경이 아닌 경우
            if (!isAccountChanged && !isSymbolChanged) {
                if (currentAccount && accountToken) {
                    const needsSubscription = ACCOUNT_TOKEN_CHANNELS.some((ch) => !subscribedChannels.value.has(ch));
                    if (needsSubscription) {
                        webSocketService.subscribeToChannels(ACCOUNT_TOKEN_CHANNELS, {
                            token: `Bearer ${accountToken}`,
                        });
                        ACCOUNT_TOKEN_CHANNELS.forEach((ch) => subscribedChannels.value.add(ch));
                    }
                }
                else {
                    const needsSubscription = PUBLIC_CHANNELS.some((ch) => !subscribedChannels.value.has(ch));
                    if (needsSubscription) {
                        webSocketService.subscribeToChannels(PUBLIC_CHANNELS);
                        PUBLIC_CHANNELS.forEach((ch) => subscribedChannels.value.add(ch));
                    }
                }
                // orderbook 채널 구독 (ws 토큰은 항상 전달)
                if (currentSymbolTicker) {
                    const orderBookChannels = [
                        'market.orderbook',
                        'market.orderbook.cancel',
                    ];
                    const needsOrderBook = orderBookChannels.some((ch) => !subscribedChannels.value.has(ch));
                    if (needsOrderBook) {
                        webSocketService.subscribeToChannels(orderBookChannels, {
                            stockCd: currentSymbolTicker,
                            ...(accountToken && { token: `Bearer ${accountToken}` }),
                        });
                        orderBookChannels.forEach((ch) => subscribedChannels.value.add(ch));
                    }
                }
                // 초기 구독 처리 완료 후 이전 값 업데이트
                previousSymbol.value = currentSymbolTicker;
            }
        }
        catch (error) {
            console.error('[useWebSocketSubscription] 구독/해제 실패:', error);
        }
    };
    /**
     * 글로벌 자산 영역용 WebSocket 채널 구독 업데이트
     * - 모든 화면에서 계좌/토큰 변경 시 호출됨 (websocket 플러그인 watch)
     * - 계좌 변경 시: 이전 계좌의 market.quote, market.ticker, private 구독 해제 후 새 토큰으로 재구독
     * - account가 있는 경우: market.quote, market.ticker와 private를 함께 구독 (token 필요)
     * - account가 없는 경우: market.quote, market.ticker만 구독 (token 불필요)
     */
    const updateAssetWebSocketSubscriptions = async () => {
        // WebSocket 연결 상태 확인 및 필요 시 연결
        await ensureWebSocketConnection();
        const accountToken = finalDeps.getAccountToken();
        const currentAccount = finalDeps.getSelectedAccountNo();
        try {
            // 1. 계좌 변경 시: market.quote, market.ticker, private 구독 해제 후 재구독
            const accountChangeHandled = handleAccountChangeResubscription({
                currentAccount,
                accountToken,
            });
            if (accountChangeHandled) {
                return;
            }
            // 2. 계좌 변경이 아닌 경우: 초기 구독 또는 누락된 채널 구독
            if (currentAccount && accountToken) {
                const needsSubscription = ACCOUNT_TOKEN_CHANNELS.some((ch) => !subscribedChannels.value.has(ch));
                if (needsSubscription) {
                    webSocketService.subscribeToChannels(ACCOUNT_TOKEN_CHANNELS, {
                        token: `Bearer ${accountToken}`,
                    });
                    ACCOUNT_TOKEN_CHANNELS.forEach((ch) => subscribedChannels.value.add(ch));
                }
            }
            else {
                const needsSubscription = PUBLIC_CHANNELS.some((ch) => !subscribedChannels.value.has(ch));
                if (needsSubscription) {
                    webSocketService.subscribeToChannels(PUBLIC_CHANNELS);
                    PUBLIC_CHANNELS.forEach((ch) => subscribedChannels.value.add(ch));
                }
            }
        }
        catch (error) {
            console.error('[useWebSocketSubscription] 자산 구독 업데이트 실패:', error);
        }
    };
    /**
     * 모든 구독 해제 (전역 연결은 유지)
     */
    const unsubscribeAll = async () => {
        try {
            // 모든 구독 해제 (전역 WebSocket 연결은 유지)
            webSocketService.unsubscribeAll();
            subscribedChannels.value.clear();
        }
        catch (error) {
            console.error('[useWebSocketSubscription] 구독 해제 실패:', error);
        }
    };
    return {
        // 상태
        subscribedChannels: subscribedChannels.value,
        // 함수들
        updateWebSocketSubscriptions,
        updateAssetWebSocketSubscriptions,
        ensureWebSocketConnection,
        unsubscribeAll,
    };
}
//# sourceMappingURL=useWebSocketSubscription.js.map